import React from "react";
import { H1, H3 } from "@datapay-by-datacom/design-system";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useOrgCtx, useOrgParams, usePermissions } from "../../hooks";
import { OrgNavTabs } from "../OrgNavTabs";
import styles from "./OrgView.module.css";
import { UnauthorizedMessage } from "../../../shared/components";
import { Org } from "../../../generated/core-api";
import { api } from "../../../api";

interface OrgViewProps {
  title: string;
  children: (org: Org) => React.ReactNode;
}

export function OrgView({ title, children }: OrgViewProps) {
  const { category, id } = useOrgParams();
  const orgCtx = useOrgCtx();
  const { canViewOrg, isProvisioner } = usePermissions();

  // Find the org from user's own orgs first
  const ownOrg = orgCtx.orgs.find(
    (org) => org.id === id && org.category === category
  );

  // For public orgs where the user isn't a member, fetch the org directly
  const publicOrgQuery = useQuery({
    queryKey: ["getOrg", category, id],
    queryFn: () => api.getOrg(category, id),
    enabled: !ownOrg && category === "public",
    refetchInterval: false,
  });

  const org = ownOrg ?? publicOrgQuery.data;

  // If the user is not authorized to view this org, show unauthorized message
  if (!canViewOrg(id, category)) {
    return <UnauthorizedMessage />;
  }

  // If org can't be found (still loading or doesn't exist), show a message
  if (!org) {
    if (publicOrgQuery.isLoading) return null;
    return <UnauthorizedMessage backLabel="< Knowledge Bases" />;
  }

  const userRole = orgCtx.ownRoleInOrg(id);

  return (
    <div className={styles.container}>
      <Link to="/org" className={styles.backLink}>
        &lt; Knowledge Bases
      </Link>
      <H1>{title}</H1>
      <div className={styles.orgHeader}>
        <H3>
          Organisation: {org.name} ({org.category})
        </H3>
        {isProvisioner() ? (
          <span className={styles.roleLabel}>[Provisioner]</span>
        ) : userRole ? (
          <span className={styles.roleLabel}>[{userRole.role}]</span>
        ) : null}
      </div>

      <OrgNavTabs />

      {children(org)}
    </div>
  );
}
